/**
 * Cockpit visual editor — the NODE TREE for the active page.
 *
 * Renders the current page's component tree as an indented list. Selecting a row
 * opens its props form (parent owns selection state). Each row offers move
 * up/down + delete (the page root is non-deletable + non-movable). The selected
 * row is also the default insert parent for the palette.
 */

import { ChevronUp, ChevronDown, Trash2, Box } from 'lucide-react';
import { getCockpitComponent } from '@argusai/shared';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { flattenPageTree, type FlatTreeRow } from './cockpit-editor-model';
import type { CockpitNode } from '@argusai/shared';

export interface CockpitTreeProps {
  root: CockpitNode;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
}

export function CockpitTree({
  root,
  selectedId,
  onSelect,
  onMove,
  onDelete,
  disabled,
}: CockpitTreeProps) {
  const rows = flattenPageTree(root);
  // Count siblings per parent so move up/down can disable at the ends.
  const siblingCount = new Map<string | null, number>();
  for (const r of rows) {
    siblingCount.set(r.parentId, (siblingCount.get(r.parentId) ?? 0) + 1);
  }

  return (
    <div className="flex flex-col py-1" role="tree" aria-label="Cockpit component tree">
      {rows.map((row) => (
        <TreeRow
          key={row.node.id}
          row={row}
          selected={row.node.id === selectedId}
          isRoot={row.parentId === null}
          siblings={siblingCount.get(row.parentId) ?? 1}
          onSelect={() => onSelect(row.node.id)}
          onMove={(d) => onMove(row.node.id, d)}
          onDelete={() => onDelete(row.node.id)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function TreeRow({
  row,
  selected,
  isRoot,
  siblings,
  onSelect,
  onMove,
  onDelete,
  disabled,
}: {
  row: FlatTreeRow;
  selected: boolean;
  isRoot: boolean;
  siblings: number;
  onSelect: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  const { node, depth, childIndex } = row;
  const contract = getCockpitComponent(node.component);
  const label = contract?.label ?? node.component;
  // A human-ish secondary label from the node's title/label prop.
  const sub =
    (node.props?.title as string | undefined) ??
    (node.props?.label as string | undefined) ??
    undefined;
  const canMoveUp = !isRoot && childIndex > 0;
  const canMoveDown = !isRoot && childIndex < siblings - 1;

  return (
    <div
      role="treeitem"
      aria-selected={selected}
      className={cn(
        'group flex items-center gap-1 rounded-md pr-1 transition-colors',
        selected ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-accent/60',
      )}
      style={{ marginLeft: `${depth * 12}px` }}
    >
      <button
        type="button"
        onClick={onSelect}
        data-testid={`cockpit-tree-node-${node.id}`}
        className="flex min-w-0 flex-1 items-center gap-1.5 py-1 pl-1.5 text-left"
      >
        <Box className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="truncate text-xs font-medium">{label}</span>
        {sub ? (
          <span className="truncate text-[11px] text-muted-foreground">— {sub}</span>
        ) : null}
        {isRoot ? (
          <span className="ml-1 shrink-0 rounded bg-muted px-1 text-[9px] font-semibold uppercase text-muted-foreground">
            page
          </span>
        ) : null}
      </button>
      {!isRoot && !disabled ? (
        <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 data-[selected=true]:opacity-100"
          data-selected={selected}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button" variant="ghost" size="icon"
                className="h-5 w-5 text-muted-foreground"
                disabled={!canMoveUp}
                onClick={() => onMove('up')}
                aria-label="Move up"
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Move up</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button" variant="ghost" size="icon"
                className="h-5 w-5 text-muted-foreground"
                disabled={!canMoveDown}
                onClick={() => onMove('down')}
                aria-label="Move down"
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Move down</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button" variant="ghost" size="icon"
                className="h-5 w-5 text-muted-foreground hover:text-destructive"
                onClick={() => onDelete()}
                aria-label="Delete component"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete this component</TooltipContent>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}
